/**
 * Landing — the first contact surface, rebuilt as a full store-style marketing page ("Broadcast"
 * visual upgrade). Concise info, one call to action, ambient bed underneath.
 *
 * The entry gate is hard (locked decision): nothing here previews gameplay. Signed-in visitors
 * get "enter the band" instead of the sign-in button. The CTA routing is unchanged — this pass is
 * presentation and copy only.
 */
import { h } from '../dom.js';
import { t, type StringKey } from '../../i18n/index.js';
import { banner, brandLogo, footer, studioLogo } from '../components/chrome.js';
import { signInUrl } from '../../api/client.js';
import type { MeResponse } from '../../api/types.js';
import { navigate } from '../router.js';
import { isRunning, onAudioStateChange, resumeAudio } from '../../audio/context.js';
import type { View } from '../view.js';

export interface LandingContext {
  me: MeResponse;
  /** `?error=` carried back from a failed OAuth round-trip. */
  error: string | null;
}

const TAGS = [
  'horror',
  'mystery',
  'atmospheric',
  'audio',
  'investigation',
  'voice',
  'singleplayer',
  'presence',
  'psychological',
] as const;

const FEATURES = ['tune', 'sound', 'speak', 'solve', 'ghosts', 'roam'] as const;
type FeatureKey = (typeof FEATURES)[number];

const INFO = ['perspective', 'play', 'enjoyed'] as const;

/** Minimal line-icons for the feature list. Stroke inherits currentColor; decorative (aria-hidden). */
const FEATURE_ICONS: Record<FeatureKey, string> = {
  tune: '<circle cx="12" cy="12" r="8.5"/><path d="M12 12l5-3"/><path d="M12 3.5v2M12 18.5v2M3.5 12h2M18.5 12h2"/>',
  sound:
    '<path d="M4 9v6h3l5 4V5L7 9H4z"/><path d="M16 8.5a4.5 4.5 0 0 1 0 7"/><path d="M18.5 6a8 8 0 0 1 0 12"/>',
  speak:
    '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M6 11a6 6 0 0 0 12 0"/><path d="M12 17v3.5"/>',
  solve:
    '<circle cx="11" cy="11" r="6.5"/><path d="M20 20l-4.4-4.4"/><path d="M11 8.5v5M8.5 11h5"/>',
  ghosts:
    '<path d="M5 19V10a7 7 0 0 1 14 0v9l-2.3-1.6L14 19l-2-1.6L10 19l-2.7-1.6z"/><path d="M9.5 10.5h.01M14.5 10.5h.01"/>',
  roam: '<path d="M3 12h4l2.5-6 5 15 2.5-9H21"/>',
};

function featureIcon(name: FeatureKey): HTMLElement {
  return h('span', {
    class: 'nb-feature__icon',
    'aria-hidden': 'true',
    html: `<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${FEATURE_ICONS[name]}</svg>`,
  });
}

export function landingView({ me, error }: LandingContext): View {
  const errorKey = error ? (`error.${error}` as StringKey) : null;
  const errorText = errorKey ? t(errorKey) : null;

  const audioNote = h('button', {
    class: 'nb-audio-state',
    type: 'button',
    'aria-live': 'polite',
    text: isRunning() ? t('landing.audioOn') : t('landing.audioPrompt'),
    onclick: () => void resumeAudio(),
  });

  const unsubscribe = onAudioStateChange((running) => {
    audioNote.textContent = running ? t('landing.audioOn') : t('landing.audioPrompt');
  });

  const cta = me.authenticated
    ? h('a', {
        class: 'nb-button nb-button--primary nb-button--cta',
        href: '#',
        text: t('landing.continue'),
        onclick: (event: Event) => {
          event.preventDefault();
          // A consented player enters at the Main Menu (Continue / Load / Settings), not straight
          // into the receiver; the menu is the hub.
          navigate(me.canPlay ? 'menu' : 'consent');
        },
      })
    : h('a', {
        class: 'nb-button nb-button--primary nb-button--cta',
        href: signInUrl('/menu'),
        text: t('landing.enter'),
      });

  const hero = h(
    'section',
    { class: 'nb-hero' },
    h('div', { class: 'nb-hero__bg', 'aria-hidden': 'true' }),
    h(
      'div',
      { class: 'nb-hero__inner' },
      h('p', { class: 'nb-eyebrow', text: t('app.tagline') }),
      h('h1', { class: 'nb-hero__title' }, brandLogo('nb-hero__logo', t('app.name'))),
      h('p', { class: 'nb-hero__lead', text: t('landing.heroTagline') }),
      errorText ? banner('alert', errorText) : null,
      !me.googleConfigured ? banner('alert', t('landing.unconfigured')) : null,
      h(
        'div',
        { class: 'nb-hero__actions' },
        cta,
        h('p', { class: 'nb-hint', text: t('landing.enterHint') }),
      ),
      audioNote,
    ),
  );

  const studio = h(
    'section',
    { class: 'nb-studio' },
    studioLogo('nb-studio__logo', t('landing.studio.name')),
    h(
      'div',
      { class: 'nb-studio__lines' },
      h('span', { class: 'nb-studio__role', text: t('landing.studio.role') }),
      h('span', { class: 'nb-studio__name', text: t('landing.studio.name') }),
    ),
    h('span', { class: 'nb-studio__web', text: t('landing.studio.web') }),
  );

  const tags = h(
    'section',
    { class: 'nb-tags', 'aria-label': t('landing.tags.label') },
    ...TAGS.map((tag) =>
      h('span', { class: 'nb-tag', text: t(`landing.tag.${tag}` as StringKey) }),
    ),
  );

  const pullquote = h(
    'blockquote',
    { class: 'nb-pullquote' },
    h('p', { text: t('landing.pullquote') }),
  );

  const about = h(
    'section',
    { class: 'nb-about' },
    h('h2', { class: 'nb-section__title', text: t('landing.about.title') }),
    h('p', { text: t('landing.about.p1') }),
    h('p', { text: t('landing.about.p2') }),
    h('p', { text: t('landing.about.p3') }),
  );

  const features = h(
    'section',
    { class: 'nb-features' },
    h('h2', { class: 'nb-section__title', text: t('landing.features.title') }),
    h(
      'div',
      { class: 'nb-features__grid' },
      ...FEATURES.map((key) =>
        h(
          'article',
          { class: 'nb-feature' },
          featureIcon(key),
          h(
            'div',
            { class: 'nb-feature__body' },
            h('h3', {
              class: 'nb-feature__title',
              text: t(`landing.feature.${key}.title` as StringKey),
            }),
            h('p', {
              class: 'nb-feature__text',
              text: t(`landing.feature.${key}.body` as StringKey),
            }),
          ),
        ),
      ),
    ),
  );

  const midband = h(
    'section',
    { class: 'nb-midband' },
    h('p', { class: 'nb-midband__q', text: t('landing.midband.q') }),
    h('p', { class: 'nb-midband__a', text: t('landing.midband.a') }),
  );

  const info = h(
    'section',
    { class: 'nb-info' },
    ...INFO.map((key) =>
      h(
        'div',
        { class: 'nb-infocard' },
        h('span', {
          class: 'nb-infocard__label',
          text: t(`landing.info.${key}.label` as StringKey),
        }),
        h('span', {
          class: 'nb-infocard__value',
          text: t(`landing.info.${key}.value` as StringKey),
        }),
      ),
    ),
  );

  const trust = h(
    'ul',
    { class: 'nb-trust' },
    h('li', { text: t('landing.bullet.saves') }),
    h('li', { text: t('landing.bullet.access') }),
  );

  const element = h(
    'div',
    { class: 'nb-page nb-page--landing' },
    hero,
    h(
      'main',
      { class: 'nb-landing', id: 'main' },
      studio,
      tags,
      pullquote,
      about,
      features,
      midband,
      info,
      trust,
    ),
    footer(),
  );

  return { element, dispose: unsubscribe };
}
